/**
 * File Preview Component - Shell/Orchestrator
 *
 * Global overlay component for previewing files, images, videos, PDFs, and articles.
 * Manages keyboard navigation and composes sub-components.
 *
 * Architecture:
 * - This component is placed at app root level (app.component.html)
 * - State managed by FilePreviewService (signals-based)
 * - Sub-components handle specific functionality (toolbar, content, info panel, filmstrip)
 */

import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  computed,
  signal,
  effect,
  untracked,
  OnInit,
  OnDestroy,
  viewChild,
  ElementRef,
  NgZone,
  AfterViewInit,
  ChangeDetectionStrategy,
  Injector,
  afterNextRender
} from '@angular/core';

import { CommonModule, NgComponentOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowLeft,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCompress,
  faExpand,
  faHourglass,
  faLocationDot,
  faMinus,
  faPlay,
  faPlus,
  faShareNodes,
  faSidebarFlip,
  faSpinner,
  faStar,
  faStar as faStarSolid,
  faTimes,
  faTriangleExclamation,
  faBookmark,
  faBrowser,
  faThumbtack,
} from '@fortawesome/pro-duotone-svg-icons';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { LoadFailedComponent } from '../page-states/load-failed.component';
import { pdfEngineCanRun } from '../../utils/pdf-engine-floor';
import { MenuModule } from 'primeng/menu';

import { FilePreviewService } from '../../services/file-preview.service';
import { commentsArmed, threadParentFor } from '../../services/file-preview-context';
import { can } from '../../models/access.model';
import {
  ZOOM_MIN,
  ZOOM_MAX,
  ZOOM_STEP,
  ANNOTATION_DRAG_THRESHOLD_PX,
  AnnotationPin,
  VideoFramePin,
  VideoTimecodeMarker,
  VideoData,
  buildImageDownloadOptions
} from './file-preview.model';
import { fitImageBox, imageFractionToScreen, screenToImageFraction, type Box, type Point, type Size } from './coordinate-mapping.utils';
import { Item } from '../../models/item';
import { CommentService } from '../../services/comment.service';
import { assignAnchorOrdinals, type Comment, type CommentAnchor } from '../../models/comment.models';
import { DownloadService } from '../../services/download.service';
import { AppConfigService } from '../../services/app-config.service';
import { MessageService, type MenuItem } from 'primeng/api';
import { ItemService } from '../../services/item.service';
import { AiDisclosureChipComponent } from '../ai-disclosure-chip/ai-disclosure-chip.component';
import type { PublicAiDisclosure } from '../../models/compliance.models';
import { BookmarkService, bookmarkLabelKey } from '../../services/bookmark.service';
import { FeaturedUndoService } from '../../services/featured-undo.service';
import { TemporalContextService } from '../../services/temporal-context.service';
import { PdfViewerComponent, type PdfPageAnnotation } from '../pdf-viewer/pdf-viewer.component';
import { getDocumentUrl, getTileState, getPreviewUrl, TileState } from '../../utils/file-url.utils';
import { getItemIcon, getItemIconColor } from '../../utils/icon-utils';
import { articleBodyState, servingBody } from '../../utils/article-body-state';
import { caughtErrorDetail } from '@branduals/graphql-errors';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { LocaleService } from '../../services/locale.service';
import { ShareDialogService } from '../share-dialog/share-dialog.service';
import { Router } from '@angular/router';
import { buildItemUrl, previewedFileDestination, previewedFileRoadThrough, shareUrl } from '../../utils/url.utils';
import { VideoPlayerComponent } from './components/video-player/video-player.component';
import { PreviewInfoComponent } from './components/preview-info/preview-info.component';
import { UntranslatedStateComponent } from '../untranslated-state/untranslated-state.component';
import { KeyboardShortcutRegistryService } from '../../services/keyboard-shortcut-registry.service';
import { CommandPaletteService } from '../command-palette/command-palette.service';
import { digitCodeMatch, hasOpenPrimeDialog, isTypingTarget } from '../../utils/keyboard.utils';
import { ThemeService } from '../../services/theme.service';
import { SystemAccessService } from '../../services/system-access.service';
import { MembershipService } from '../../services/membership.service';
import { UNSAVED_WORK_MOUNT, UnsavedWorkPromptService, claimEscape, isEscapeClaimed, type UnsavedWorkMount } from '@branduals/dialog-guard';
import {
  faCircleMinus,
} from '@fortawesome/pro-duotone-svg-icons';

@Component({
  selector: 'app-file-preview',
  standalone: true,
  imports: [
    AiDisclosureChipComponent,
    CommonModule,
    NgComponentOutlet,
    FormsModule,
    TranslateModule,
    FontAwesomeModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
    LoadFailedComponent,
    MenuModule,
    PdfViewerComponent,
    SafeHtmlPipe,
    VideoPlayerComponent,
    PreviewInfoComponent,
    UntranslatedStateComponent
  ],
  templateUrl: './file-preview.component.html',
  styleUrls: ['./file-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // The preview is a dismissable app-chrome CONTAINER: a registrant mounted inside it (the
    // compliance lock reason) reads its mount here — the preview's own close destroys the work
    // (so the container is this preview's service), while in-app route changes do NOT (the
    // overlay survives them — routePrefix '/' = the whole app shell), and only a clock
    // transition, which force-closes every overlay, still counts it (the prefix rung yields to
    // the clock bypass). The guarded close asks through the one leave prompt (ruled 2026-09-01).
    {
      provide: UNSAVED_WORK_MOUNT,
      useFactory: (): UnsavedWorkMount => ({ routePrefix: '/', container: inject(FilePreviewService) }),
    },
  ],
})
export class FilePreviewComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private mediaQueryCleanup: (() => void) | null = null;

  // Services
  readonly previewService = inject(FilePreviewService);
  protected readonly access = inject(SystemAccessService);
  private readonly membership = inject(MembershipService);
  /** Bookmark, featured and share are a MEMBER's writes — a public visitor sees none; a monitoring root counts. */
  protected readonly isMember = this.membership.isAdmittedOrRoot;
  /** A public visitor reads no comments: the annotation layer and the timestamp capture belong to comments. */
  protected readonly publicMode = this.membership.publicMode;
  private readonly downloadService = inject(DownloadService);
  private readonly configService = inject(AppConfigService);
  private readonly messageService = inject(MessageService);
  private readonly featuredUndo = inject(FeaturedUndoService);
  private readonly translateService = inject(TranslateService);
  private readonly itemService = inject(ItemService);
  /** Template-read: the toolbar star's label names the unknown state's reason from the service. */
  protected readonly bookmarkService = inject(BookmarkService);
  private readonly localeService = inject(LocaleService);
  protected readonly temporal = inject(TemporalContextService);
  private readonly shareDialogService = inject(ShareDialogService);
  protected readonly locale = this.localeService.activeContentLanguage;

  /** The article arm's headline: the one editorial read label (the content-lens title, else the name, else the two-state word). */
  protected readonly articleHeadline = computed(() => this.localeService.editorialLabel(this.previewService.item()));
  /** The article arm's body state — the one rule the three article doors share (content · untranslated · empty). */
  protected readonly articleState = computed(() => articleBodyState(this.previewService.item(), this.locale()));
  /** The article arm's body — the lens's own, or the same-language variant that serves (the body rule's render read). */
  protected readonly articleBody = computed(() => servingBody(this.previewService.item()?.text, this.locale()));

  // FontAwesome icons
  readonly faArrowLeft = faArrowLeft;
  readonly faChevronLeft = faChevronLeft;
  readonly faChevronRight = faChevronRight;
  readonly faStar = faStar;
  readonly faStarSolid = faStarSolid;
  readonly faTimes = faTimes;
  readonly faExpand = faExpand;
  readonly faCompress = faCompress;
  readonly faMinus = faMinus;
  readonly faPlus = faPlus;
  readonly faShareNodes = faShareNodes;
  readonly faSidebarFlip = faSidebarFlip;
  readonly faPlay = faPlay;
  readonly faBookmark = faBookmark;
  readonly faHourglass = faHourglass;
  readonly faSpinner = faSpinner;
  readonly faTriangleExclamation = faTriangleExclamation;
  readonly faBrowser = faBrowser;
  readonly faCircleMinus = faCircleMinus;
  readonly faThumbtack = faThumbtack;
  readonly faChevronDown = faChevronDown;

  /** True when the context enables compare mode AND the preview isn't in 1×1 single-pane view (i.e. at least 2 pinned). */
  protected readonly compareEnabled = computed(() => !!this.previewService.context()?.compare?.enabled);

  /** True when the currently-visible item is already pinned to compare. */
  protected readonly currentItemPinned = computed(() => {
    const id = this.previewService.item()?.id;
    return id ? this.previewService.pinnedIds().has(id) : false;
  });

  /** Tooltip key for the pin button — reflects pinned state + whether we're at the pin cap. */
  protected readonly pinTooltipKey = computed(() => {
    if (this.currentItemPinned()) return 'filePreview.compare.unpin';
    if (!this.previewService.canPinMore()) return 'filePreview.compare.pinFull';
    return 'filePreview.compare.pin';
  });

  /** The bottom control row's tools cluster (annotate / pin-to-compare) has content.
   *  These are workflow gestures, so they render beside the context's action bar —
   *  where the decision/vote controls live — not among the generic toolbar icons. */
  protected readonly viewerToolsVisible = computed(() =>
    this.annotateAvailable() || this.compareEnabled());

  /** Click handler for the pin-to-compare button — toggles pin state on the current item. */
  togglePinCurrent(): void {
    const id = this.previewService.item()?.id;
    if (!id) return;
    if (this.currentItemPinned()) {
      this.previewService.unpinFromCompare(id);
    } else {
      this.previewService.pinToCompare(id);
    }
  }

  // Keyboard registry integration for context-provided bindings.
  // Universal @HostListener below retains hardcoded Escape/arrows/zoom; registry handles
  // context.keyBindings (P/X/U/digits/Alt+... from the Selects vote bar context, etc.).
  private readonly registry = inject(KeyboardShortcutRegistryService);
  private readonly contextBindingDisposers: Array<() => void> = [];
  private readonly commandPalette = inject(CommandPaletteService);

  /** Raw focus request — consumers read the CLAMPED projection below, never this. */
  private readonly focusedPaneRequest = signal(0);

  /** Items pinned for compare, preserving items() order for stable pane positioning. */
  protected readonly orderedPinnedIds = computed(() => {
    const pinned = this.previewService.pinnedIds();
    const allItems = this.previewService.items();
    return allItems.map(i => i.id).filter(id => pinned.has(id));
  });

  /** Which pane has keyboard focus in compare mode (0-based) — clamped into the LIVE pinned
   *  set. An unpin below the focused index (or a stale index surviving from an earlier
   *  compare session on this app-root singleton) must never leave the visible focus marker
   *  and the audit-weight key dispatch pointing at different panes: both read this one
   *  clamped value, so a focused pane is always a rendered pane. */
  protected readonly focusedPaneIndex = computed(() => {
    const n = this.orderedPinnedIds().length;
    return n === 0 ? 0 : Math.min(this.focusedPaneRequest(), n - 1);
  });

  protected itemById(id: string): Item | undefined {
    return this.previewService.items().find(i => i.id === id);
  }

  protected setFocusedPane(index: number): void {
    this.focusedPaneRequest.set(index);
  }

  protected unpinPane(paneId: string, ev: MouseEvent): void {
    ev.stopPropagation();
    this.previewService.unpinFromCompare(paneId);
  }

  /** Resolve actionBar inputs for a specific item/pane. Prefers the context's
   *  `inputsFor(item)` hook (for per-pane-dynamic inputs like entryId + currentVote);
   *  falls back to the static `inputs` dict. Returns ONLY what the context declares —
   *  never injects `item` or other bindings the target component may not declare. */
  protected actionBarInputsForPane(item: Item | null): Record<string, unknown> {
    if (!item) return {};
    const ab = this.previewService.context()?.actionBar;
    if (!ab) return {};
    if (ab.inputsFor) return ab.inputsFor(item);
    return ab.inputs ?? {};
  }

  /** True when the context mounts ONE container-level action bar below the compare
   *  panes (a review-level verdict has a single subject) instead of one bar per pane. */
  protected readonly globalActionBar = computed(() => {
    const ab = this.previewService.context()?.actionBar;
    return !!ab?.component && ab.mode === 'global';
  });

  /** Context opt-in: visibly mark the keyboard-focused pane (audit-weight casts must
   *  never dispatch at an unindicated pane). */
  protected readonly showPaneFocus = computed(() =>
    this.previewService.context()?.compare?.focusedPaneIndicator === true);

  /** Context-supplied pane label (item name for Reviews); null falls back to "Pane N". */
  protected paneLabelFor(item: Item): string | null {
    return this.previewService.context()?.compare?.paneLabel?.(item) ?? null;
  }

  // Local state
  readonly zoom = signal(1);
  /** Pan offset in SCREEN pixels — applied before scale (`translate(pan) scale(zoom)`),
   *  so dragging moves the image 1:1 with the pointer at every zoom level. */
  readonly pan = signal<{ x: number; y: number }>({ x: 0, y: 0 });
  /** True while a pointer drag is panning the image (disables the transform transition). */
  readonly isPanning = signal(false);
  private panPointerId: number | null = null;
  private panStart = { x: 0, y: 0 };
  private panOrigin = { x: 0, y: 0 };
  readonly videoData = signal<VideoData | null>(null);
  readonly videoLoading = signal(false);
  private readonly ngZone = inject(NgZone);
  private readonly themeService = inject(ThemeService);

  // ============================================================================
  // Image annotations — state
  // ============================================================================

  /** True while the annotate gesture owns image pointer events (pan is suspended). */
  readonly annotateMode = signal(false);
  /** The viewer-image container's box: origin = the element's client-space corner
   *  (the overlay's coordinate origin), size = the CONTENT box — the area the img's
   *  max-width/max-height actually resolve against. Measuring the padded border box
   *  here inflates the fit math and makes stored anchor fractions viewport-dependent
   *  (the same image point stores different fractions at different window sizes).
   *  Size from the ResizeObserver; origin re-measured at gesture start (the box can
   *  move without resizing). */
  readonly viewerBox = signal<Box | null>(null);
  /** The container's padding offsets — where the content box starts in overlay space. */
  readonly viewerPad = signal<Point>({ x: 0, y: 0 });
  /** Natural pixel size of the displayed image (from the img load event) — the
   *  basis for fraction coordinates. */
  readonly naturalSize = signal<Size | null>(null);
  /** The current entry thread's comments — the overlay's pin data source. */
  protected readonly annotationComments = signal<Comment[]>([]);
  /** The pins' read FAILED for the current media — a compact face floats over the stage, never an
   *  image, video or PDF that merely looks un-annotated. Cleared at the start of every pin read and
   *  whenever the pin effect stands the overlay down. */
  readonly annotationsLoadFailed = signal(false);
  /** The pin read is in the air behind the face's Try again. */
  readonly annotationsReloading = signal(false);

  /** Monotonically increasing pin-read token, bumped on EVERY run of the pin-load
   *  effect (bail-outs included). An anchored read publishes only when its captured
   *  token is still current — a slow read landing after a later run (navigation,
   *  a deletion's version bump, a "Time travel" flip) must never re-show stale pins. */
  private anchoredReadToken = 0;
  /** In-flight annotate gesture in image-fraction space (start = pointer-down). */
  protected readonly annotationDraft = signal<{ start: Point; current: Point } | null>(null);
  private annotationPointerId: number | null = null;
  private annotationDownScreen: Point | null = null;
  private resizeObserver: ResizeObserver | null = null;
  readonly viewerImageEl = viewChild<ElementRef<HTMLDivElement>>('viewerImage');
  /** The mounted video player (video previews only) — the timestamp stamp reads its
   *  playhead and pauses it. */
  private readonly videoPlayerCmp = viewChild(VideoPlayerComponent);
  /** Anchor navigation targets — present only while their media branch is mounted. The PDF viewer is
   *  reached through its template ref, never the class: a runtime reference to the class here would
   *  keep its import static and drag the PDF engine back into the boot path. */
  readonly pdfViewer = viewChild<PdfViewerComponent>('pdfViewer');
  readonly videoPlayer = viewChild(VideoPlayerComponent);
  private readonly commentService = inject(CommentService);
  private readonly leavePrompt = inject(UnsavedWorkPromptService);
  readonly faLocationDot = faLocationDot;

  // Mobile / Bottom sheet state
  readonly isMobile = signal(false);
  readonly bottomSheetExpanded = signal(false);
  private bottomSheetDragStartY = 0;
  private bottomSheetStartHeight = 0;
  readonly bottomSheetRef = viewChild<ElementRef<HTMLDivElement>>('bottomSheet');
  /** The overlay's root — takes focus on open so the opener's own key handlers stand down. */
  readonly overlayRoot = viewChild<ElementRef<HTMLDivElement>>('overlayRoot');

  // Track previous item ID to detect changes
  private previousItemId: string | null = null;

  // Forced-dark push/pop must pair exactly once per open — tracked across effect runs
  private wasPreviewOpen = false;
  /** The element focused when the preview opened; focus returns to it on close (a11y focus-return). */
  private opener: HTMLElement | null = null;

  constructor() {
    // The preview is a full-viewport dark experience: while open, force the app theme dark
    // so token-styled children (governance sections, label-field, badges) and body-appended
    // overlays spawned from the preview all resolve dark. Transition-guarded — this app-root
    // singleton's effect re-runs on any isOpen() change; push only on false→true, pop only
    // on true→false. DestroyRef guard releases a held push if destroyed mid-open.
    //
    // The same transition moves FOCUS: the overlay owns it while open (the slide panel's and
    // the public lightbox's shape). The element that opened the preview — a grid tile, a list
    // row — keeps its own arrow / Enter / Space handlers, so leaving focus there would let the
    // tile walk its hidden focus, re-open or toggle a selection under the overlay while the
    // viewer's own document-level arrows never fire. Focus lands on the overlay root after it
    // renders and returns to the opener on close.
    effect(() => {
      const open = this.previewService.isOpen();
      if (open === this.wasPreviewOpen) return;
      this.wasPreviewOpen = open;
      if (open) {
        this.themeService.pushForcedDark();
        this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        afterNextRender(() => this.overlayRoot()?.nativeElement.focus(), { injector: this.injector });
      } else {
        this.themeService.popForcedDark();
        const opener = this.opener;
        this.opener = null;
        if (opener && document.contains(opener)) opener.focus();
      }
    });
    this.destroyRef.onDestroy(() => {
      if (this.wasPreviewOpen) {
        this.themeService.popForcedDark();
      }
    });
    // Reset per-item view state when the item changes (navigation or reopen). This is
    // the ONE home for the zoom/pan reset — navigation paths never reset individually,
    // so re-selecting the CURRENT item deliberately keeps the zoom (it is not an item
    // change). Closing the preview resets separately in close(). Annotation state is
    // per-item too: the mode, the measured natural size, and any pending composer
    // handoff all end here (a pending anchor must never land on a different item).
    effect(() => {
      const item = this.previewService.item();
      const currentId = item?.id ?? null;

      if (currentId !== this.previousItemId) {
        this.videoData.set(null);
        this.videoLoading.set(false);
        this.resetZoomPan();
        this.exitAnnotateMode();
        this.naturalSize.set(null);
        this.previewService.clearAnnotationHandoff();
        this.previousItemId = currentId;
      }
    });

    // Compare mode has its own layout and gesture semantics — an armed annotate
    // gesture must not survive into it.
    effect(() => {
      if (this.previewService.isCompareMode()) this.exitAnnotateMode();
    });

    // The overlay's pin data: the current thread's comments, re-fetched on item change
    // and after an annotated comment posts (the annotations version bump). Serves all
    // three anchor renderers (image overlay, PDF page layers, video markers). Empty
    // while the context disarms commenting, in "Time travel" (pins are a NOW
    // surface — one clock per render) and in public mode: the annotation layer is a
    // surface a visitor never reads, and the comments root is no public read, so the
    // ask could only raise the failure face over the image (seen on a visitor's
    // preview, 2026-10-08). Trigger signals are read first; the loader runs
    // untracked so its own reads never join this effect's producers.
    effect(() => {
      const item = this.previewService.item();
      const ctx = this.previewService.context();
      const type = this.previewService.contentType();
      this.previewService.annotationsVersion();
      const inTemporalMode = this.temporal.active();
      const inPublicMode = this.publicMode();
      const renders = type === 'image' || type === 'pdf' || type === 'video';
      // Any re-evaluation invalidates in-flight anchored reads — bail-out branches
      // included, so a read resolving after this run can never publish a stale list.
      this.anchoredReadToken++;
      if (!item || !renders || !commentsArmed(ctx) || inTemporalMode || inPublicMode) {
        untracked(() => { this.annotationComments.set([]); this.annotationsLoadFailed.set(false); });
        return;
      }
      const parentId = threadParentFor(ctx, item).parentId;
      untracked(() => void this.loadAnnotationComments(parentId));
    });

    // Anchor-chip activation → show the annotation on its media. One-shot request:
    // consumed, routed per anchor kind, cleared.
    effect(() => {
      const req = this.previewService.requestedAnchorNavigation();
      if (!req) return;
      untracked(() => {
        this.navigateToAnchor(req.commentId, req.anchor);
        this.previewService.requestedAnchorNavigation.set(null);
      });
    });

    // Track the viewer-image container's size — the annotation overlay's coordinate
    // space. The element only exists while an image is in the ready state, so the
    // viewChild signal drives attach/detach. jsdom has no ResizeObserver; the guard
    // keeps class-level specs (which set viewerBox directly) working.
    effect(() => {
      const el = this.viewerImageEl()?.nativeElement;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      if (!el) return;
      untracked(() => this.measureViewerBox(el));
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.ngZone.run(() => this.measureViewerBox(el)));
        this.resizeObserver.observe(el);
      }
    });

    // Register context-provided keybindings via the registry,
    // using effect's onCleanup hook so context changes atomically dispose prior bindings
    // before the new registration. On component destroy, ngOnDestroy disposes any
    // straggler (defensive — the effect's onCleanup also fires on destroy).
    //
    // Safe to call register/dispose from an effect: both methods use `signal.update(fn)`
    // internally, which doesn't establish a reactive dep on `_bindings` from this effect's
    // scope. The effect only reacts to `context().keyBindings` changes, as intended.
    effect((onCleanup) => {
      const ctxBindings = this.previewService.context()?.keyBindings ?? [];
      if (ctxBindings.length === 0) return;
      const dispose = this.registry.register(ctxBindings);
      this.contextBindingDisposers.push(dispose);
      onCleanup(() => {
        dispose();
        const idx = this.contextBindingDisposers.indexOf(dispose);
        if (idx >= 0) this.contextBindingDisposers.splice(idx, 1);
      });
    });
  }

  // Computed
  readonly zoomPercent = computed(() => `${Math.round(this.zoom() * 100)}%`);

  /** The image viewer's CSS transform. Translate FIRST — the reversed order would
   *  multiply the pan offset by the zoom factor and make drag speed zoom-dependent. */
  readonly imageTransform = computed(() => {
    const p = this.pan();
    return `translate(${p.x}px, ${p.y}px) scale(${this.zoom()})`;
  });

  // ============================================================================
  // Image annotations — derived state
  // ============================================================================

  /** Is the thread parented on the previewed item ITSELF? Then the item's own role is
   *  the honest permission subject. A context that parents elsewhere (Reviews threads on
   *  the review entry) is exempt by construction — the file's role must not gate a
   *  review-level conversation. */
  protected readonly gatedByItemRole = computed(() => {
    const ctx = this.previewService.context();
    const item = this.previewService.item();
    return !!item && threadParentFor(ctx, item).parentId === item.id;
  });

  /** May the member post into the thread this preview shows? Collapsed ONCE here (the
   *  host that owns annotate + [canEdit]); an unknown role reads false — write
   *  affordances stay hidden until the role confirms. */
  protected readonly effectiveCanPost = computed(() =>
    this.gatedByItemRole() ? can.addComments(this.previewService.item()?.effectiveRole) : true);

  /** May the member resolve ANY comment here (the role leg — authors keep their own). */
  protected readonly effectiveCanModerate = computed(() =>
    this.gatedByItemRole() ? can.resolveComments(this.previewService.item()?.effectiveRole) : true);

  /** Anchor chips navigate only where the annotation machinery is armed. */
  protected readonly threadArmed = computed(() => commentsArmed(this.previewService.context()));

  /** Annotating needs ready media that takes a positional gesture (image point/region,
   *  PDF page point, video frame point/region — arming pauses the video) with somewhere
   *  to thread the comment (a per-item comment context), in single view, outside
   *  "Time travel", from a member who can write. Pins still RENDER outside these
   *  conditions — this gates the gesture only. */
  protected readonly annotateAvailable = computed(() => {
    const type = this.previewService.contentType();
    return (type === 'image' || type === 'pdf' || type === 'video')
      && this.pdfRenderState() === 'ready'
      && commentsArmed(this.previewService.context())
      && this.effectiveCanPost()
      && !this.previewService.isCompareMode()
      && !this.temporal.active()
      && !this.access.isReadOnlyRoot();
  });

  /** Zoom lives in the toolbar (relocated from a floating overlay) — image-ready,
   *  single view only. */
  protected readonly zoomControlsVisible = computed(() =>
    this.previewService.contentType() === 'image'
    && this.pdfRenderState() === 'ready'
    && !this.previewService.isCompareMode());

  /** The image's zoom-1 layout box in overlay space (origin = the container's corner;
   *  the content area starts at the padding offset) — everything the annotation overlay
   *  renders is positioned against this. */
  protected readonly annotationBaseBox = computed<Box | null>(() => {
    const box = this.viewerBox();
    const nat = this.naturalSize();
    if (!box || !nat || box.width <= 0 || box.height <= 0 || nat.width <= 0 || nat.height <= 0) return null;
    const pad = this.viewerPad();
    return fitImageBox({ left: pad.x, top: pad.y, width: box.width, height: box.height }, nat);
  });

  /** Overlay pins: POINT/REGION anchors projected into container space under the
   *  current zoom/pan. Ordinals come from the shared numbering home, so a pin's
   *  number and its comment's thread chip can never disagree; anchor kinds this
   *  overlay cannot render (page/timecode) still consume their number. */
  protected readonly annotationPins = computed<AnnotationPin[]>(() => {
    const base = this.annotationBaseBox();
    if (!base) return [];
    const zoom = this.zoom();
    const pan = this.pan();
    const comments = this.annotationComments();
    const ordinals = assignAnchorOrdinals(comments);
    const pins: AnnotationPin[] = [];
    const project = (c: Comment): void => {
      const a = c.anchor;
      if (!a || (a.kind !== 'POINT' && a.kind !== 'REGION') || a.x == null || a.y == null) return;
      const topLeft = imageFractionToScreen({ x: a.x, y: a.y }, base, zoom, pan);
      if (a.kind === 'POINT') {
        pins.push({ id: c.id, ordinal: ordinals.get(c.id) ?? 0, kind: 'POINT', x: topLeft.x, y: topLeft.y });
      } else {
        pins.push({
          id: c.id, ordinal: ordinals.get(c.id) ?? 0, kind: 'REGION',
          x: topLeft.x, y: topLeft.y,
          width: (a.width ?? 0) * base.width * zoom,
          height: (a.height ?? 0) * base.height * zoom,
        });
      }
    };
    for (const top of comments) {
      project(top);
      for (const reply of top.replies ?? []) project(reply);
    }
    return pins;
  });

  /** The PENDING image anchor (gesture completed, comment not yet posted) rendered as a
   *  DRAFT pin/region — the mark stays visible while the user writes about it (marking and
   *  commenting are ONE operation; the mark vanishing at mouse-up left the user describing
   *  a place they could no longer see). Unnumbered: ordinals are assigned at post, and a
   *  predicted number could lie if another anchored comment lands first. Clears with the
   *  ONE pending-anchor home (post, chip dismiss, Escape, annotate-off, navigation). */
  protected readonly pendingAnnotationPin = computed<AnnotationPin | null>(() => {
    const a = this.previewService.pendingAnchor();
    const base = this.annotationBaseBox();
    if (!a || !base || (a.kind !== 'POINT' && a.kind !== 'REGION') || a.x == null || a.y == null) return null;
    const zoom = this.zoom();
    const pan = this.pan();
    const topLeft = imageFractionToScreen({ x: a.x, y: a.y }, base, zoom, pan);
    if (a.kind === 'POINT') {
      return { id: '__pending__', ordinal: 0, kind: 'POINT', x: topLeft.x, y: topLeft.y };
    }
    return {
      id: '__pending__', ordinal: 0, kind: 'REGION',
      x: topLeft.x, y: topLeft.y,
      width: (a.width ?? 0) * base.width * zoom,
      height: (a.height ?? 0) * base.height * zoom,
    };
  });

  /** The pending PAGE anchor for the PDF viewer's draft pin (same continuity rule as
   *  the image draft above — the clicked spot stays marked while the comment is written). */
  protected readonly pendingPdfAnnotation = computed<{ page: number; x?: number; y?: number } | null>(() => {
    const a = this.previewService.pendingAnchor();
    if (!a || a.kind !== 'PAGE' || a.page == null) return null;
    return { page: a.page, x: a.x ?? undefined, y: a.y ?? undefined };
  });

  /** PAGE anchors routed to the PDF viewer's per-page comment layers. Ordinals share
   *  the ONE numbering home with the thread chips (kinds the PDF cannot render still
   *  consume their number — parity by construction). */
  protected readonly pdfAnnotationPins = computed<PdfPageAnnotation[]>(() => {
    const comments = this.annotationComments();
    const ordinals = assignAnchorOrdinals(comments);
    const pins: PdfPageAnnotation[] = [];
    const project = (c: Comment): void => {
      const a = c.anchor;
      if (!a || a.kind !== 'PAGE' || a.page == null) return;
      pins.push({ id: c.id, ordinal: ordinals.get(c.id) ?? 0, page: a.page, x: a.x, y: a.y });
    };
    for (const top of comments) {
      project(top);
      for (const reply of top.replies ?? []) project(reply);
    }
    return pins;
  });

  /** TIMECODE / TIMECODE_RANGE anchors rendered as markers on the video progress bar
   *  (ranges render; range CAPTURE is deferred — v1 video production is single-timecode). */
  protected readonly videoTimecodeMarkers = computed<VideoTimecodeMarker[]>(() => {
    const comments = this.annotationComments();
    const ordinals = assignAnchorOrdinals(comments);
    const markers: VideoTimecodeMarker[] = [];
    const project = (c: Comment): void => {
      const a = c.anchor;
      if (!a || (a.kind !== 'TIMECODE' && a.kind !== 'TIMECODE_RANGE') || a.timecode == null) return;
      markers.push({ id: c.id, ordinal: ordinals.get(c.id) ?? 0, timecode: a.timecode, timecodeTo: a.timecodeTo });
    };
    for (const top of comments) {
      project(top);
      for (const reply of top.replies ?? []) project(reply);
    }
    return markers;
  });

  /** TIMECODE anchors carrying ON-FRAME geometry — the video overlay renders them as
   *  pins/regions around their own moment. Ordinals ride the ONE numbering home. */
  protected readonly videoFramePins = computed<VideoFramePin[]>(() => {
    const comments = this.annotationComments();
    const ordinals = assignAnchorOrdinals(comments);
    const pins: VideoFramePin[] = [];
    const project = (c: Comment): void => {
      const a = c.anchor;
      if (!a || a.kind !== 'TIMECODE' || a.timecode == null || a.x == null || a.y == null) return;
      pins.push({
        id: c.id, ordinal: ordinals.get(c.id) ?? 0,
        x: a.x, y: a.y,
        width: a.width ?? undefined, height: a.height ?? undefined,
        timecode: a.timecode,
      });
    };
    for (const top of comments) {
      project(top);
      for (const reply of top.replies ?? []) project(reply);
    }
    return pins;
  });

  /** The pending TIMECODE anchor's on-frame draft for the video overlay (same
   *  continuity rule as the image/PDF drafts — the mark stays while the comment is
   *  written). Plain timestamps (no geometry) draft nothing on the frame — the
   *  composer's chip is their visible half. */
  protected readonly pendingVideoFramePin = computed<{ x: number; y: number; width?: number; height?: number } | null>(() => {
    const a = this.previewService.pendingAnchor();
    if (!a || a.kind !== 'TIMECODE' || a.x == null || a.y == null) return null;
    return { x: a.x, y: a.y, width: a.width ?? undefined, height: a.height ?? undefined };
  });

  /** The in-flight draft's VISUAL rectangle (container space). Clamped to the image
   *  box for display only — the stored anchor is never clamped: releasing outside the
   *  image cancels the gesture, matching the server's reject-never-clamp posture. */
  protected readonly annotationDraftRect = computed<Box | null>(() => {
    const d = this.annotationDraft();
    const base = this.annotationBaseBox();
    if (!d || !base) return null;
    const clamp = (v: number): number => Math.max(0, Math.min(1, v));
    const x1 = clamp(Math.min(d.start.x, d.current.x));
    const y1 = clamp(Math.min(d.start.y, d.current.y));
    const x2 = clamp(Math.max(d.start.x, d.current.x));
    const y2 = clamp(Math.max(d.start.y, d.current.y));
    const zoom = this.zoom();
    const pan = this.pan();
    const tl = imageFractionToScreen({ x: x1, y: y1 }, base, zoom, pan);
    const br = imageFractionToScreen({ x: x2, y: y2 }, base, zoom, pan);
    return { left: tl.x, top: tl.y, width: br.x - tl.x, height: br.y - tl.y };
  });

  /**
   * PDF URL sourced from the server-computed `DocumentFileData.documentUrl` field.
   * The server builds this via `BlobPathBuilder.GetDocumentPath(brandId, blobName)` and only
   * emits it when processing has completed — we never have to infer paths client-side.
   */
  readonly documentUrl = computed(() => {
    const item = this.previewService.item();
    if (!item || this.previewService.contentType() !== 'pdf') return '';
    return getDocumentUrl(item, this.configService) ?? '';
  });

  /**
   * The document URL the mounted pdf viewer finished loading pages for (null = none). Set from
   * the viewer's own load lifecycle (its pdfLoadStart / pdfLoaded / pdfLoadError outputs) so
   * arrow arbitration tracks pdf.js truth, not just the server tile state: over a corrupt or
   * still-parsing document the viewer has no pages to flip, and the arrows must keep walking
   * items instead of dying. Every load start clears it — the viewer is destroyed and re-created
   * on each open, so re-opening the SAME document re-parses from zero and must not inherit the
   * previous mount's "ready"; a URL change invalidates by comparison as well.
   */
  protected readonly pdfReadyUrl = signal<string | null>(null);

  /**
   * Unified render state for the PDF / image / video viewers. Folds the full-item load
   * state, live subscription events, and server-side processingStatus into a single enum
   * so the template can switch cleanly without undefined cases. Returns `'loading'` while
   * the full item data is still being fetched to avoid flashing a FAILED empty-state for
   * items that simply haven't been populated yet.
   */
  readonly pdfRenderState = computed<TileState | 'loading'>(() => {
    if (this.previewService.loading()) return 'loading';
    const item = this.previewService.item();
    if (!item) return 'loading';
    return getTileState(item);
  });

  readonly imageUrl = computed(() => {
    const item = this.previewService.item();
    if (!item || this.previewService.contentType() !== 'image') return '';
    return getPreviewUrl(item, 'large', this.configService) ?? '';
  });

  /** Download options from the shared builder (dedupes same-dimension size rows).
   *  Videos offer the Original file only — stream variants are playback renditions,
   *  not download choices. */
  readonly downloadOptions = computed(() => {
    const item = this.previewService.item();
    const contentType = this.previewService.contentType();

    if (!item || (contentType !== 'image' && contentType !== 'video')) return [];

    return buildImageDownloadOptions(
      item,
      contentType === 'image' ? item.fileData?.formats : undefined
    );
  });

  // ============================================================================
  // Lifecycle
  // ============================================================================

  ngOnInit(): void {
    this.setupBreakpointObserver();
  }

  ngAfterViewInit(): void {
    // Bottom sheet setup happens after view init
  }

  private setupBreakpointObserver(): void {
    // Use window.matchMedia for responsive detection
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    // Set initial value
    this.isMobile.set(mediaQuery.matches);

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      this.ngZone.run(() => {
        this.isMobile.set(event.matches);
        // Reset bottom sheet state when switching to desktop
        if (!event.matches) {
          this.bottomSheetExpanded.set(false);
        }
      });
    };

    mediaQuery.addEventListener('change', handleChange);

    // Cleanup on destroy
    this.mediaQueryCleanup = () => mediaQuery.removeEventListener('change', handleChange);
  }

  ngOnDestroy(): void {
    this.mediaQueryCleanup?.();
    this.resizeObserver?.disconnect();
    // Belt-and-suspenders: the effect's onCleanup above handles the same disposal on
    // component destroy, but defensive disposal here ensures no stragglers if context
    // changed without triggering the effect's cleanup path.
    for (const d of this.contextBindingDisposers) d();
    this.contextBindingDisposers.length = 0;
  }

  // ============================================================================
  // Keyboard Navigation
  // ============================================================================

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Only handle when preview is open
    if (!this.previewService.isOpen()) return;

    // Don't intercept if user is typing in an input
    if (isTypingTarget(event)) {
      return;
    }

    // A PrimeNG dialog above the preview owns the keyboard (the share dialog, a discard prompt, a
    // picker): Escape belongs to it, and d/arrows/digits must not act on the preview underneath.
    // The command palette is not a PrimeNG dialog — its own arm sits on the Escape case below.
    if (hasOpenPrimeDialog()) return;

    // A ctrl/⌘-modified press is a browser chord (Ctrl+P prints, Ctrl+F finds, Ctrl+D bookmarks,
    // Ctrl+0 and Ctrl+= / Ctrl+- work the PAGE zoom, ⌘+← navigates Back): it never runs a viewer
    // case and is never consumed — the press falls through with its browser meaning intact. The
    // registry applies the same refusal to its own rows, so nothing downstream claims it either.
    if (event.ctrlKey || event.metaKey) return;

    // Dispatch arguments the digit branch and the default arm share. In compare mode, contexts
    // with a VISIBLE focused-pane marker dispatch at the focused pane's item — a keyboard cast
    // must land where the member can see it will land. Contexts without the marker (Selects'
    // deliberate omission) keep the current-item dispatch: routing their keys to an unindicated
    // pane would be the exact hazard the marker exists to prevent.
    const mode: 'single' | 'compare' = this.previewService.isCompareMode() ? 'compare' : 'single';
    const focusedPaneItem = mode === 'compare' && this.previewService.context()?.compare?.focusedPaneIndicator
      ? this.itemById(this.orderedPinnedIds()[this.focusedPaneIndex()] ?? '') ?? this.previewService.item()
      : this.previewService.item();
    const dispatchToRegistry = () => this.registry.handleKeyEvent(event, mode, focusedPaneItem);

    // Digit keys match on the PHYSICAL key, never the typed character: on a Czech layout the
    // unshifted top row types "+ěšč…" and a mac Option+digit types a symbol, so a glyph-keyed case
    // would never see the digit. The context's digit bindings (vote ratings) win over the viewer's
    // own zoom reset — a voter's 0 rates 10, an Observer's 0 fits — so the branch asks the registry
    // first and hands the press to it. A dispatched press NEVER continues into the switch: its
    // glyph-keyed zoom cases would eat a Czech "+" (the physical 1) before the registry could rate.
    // Alt-modified digits skip the branch and reach the registry's alt rows through the default
    // arm; a digit nobody claims keeps its glyph meaning below (a Czech "+" still zooms in).
    if (!event.altKey && /^(?:Digit|Numpad)\d$/.test(event.code)) {
      if (this.registry.hasDigitBinding(event, mode, focusedPaneItem) && dispatchToRegistry()) return;
      if (digitCodeMatch(event, '0')) {
        this.zoomReset();
        event.preventDefault();
        return;
      }
    }

    switch (event.key) {
      case 'Escape':
        // The command palette is a plain fixed overlay outside PrimeNG's layering — the dialog
        // stand-down above cannot see it — and this listener registers BEFORE the palette's own
        // (the preview mounts earlier in the app shell), so the palette's preventDefault lands
        // too late to shield the preview: an open palette owns the Escape explicitly here. The
        // shortcut reference is the same shape in reverse: this listener runs FIRST (the panel
        // mounts after the preview), stands down while the reference is open, and the panel's own
        // listener then closes it — a second Escape closes the preview.
        if (this.commandPalette.isOpen() || this.registry.helpOpen()) return;
        // A press another rung already claimed (never bare defaultPrevented — a widget's hollow
        // claim would fake it) is left alone.
        if (event.isComposing || isEscapeClaimed(event)) return;
        // Escape backs out ONE layer: an active annotate mode — or a pending
        // not-yet-posted mark — ends first; only a further Escape closes the preview.
        if (this.annotateMode() || this.previewService.pendingAnchor()) {
          this.exitAnnotateMode();
        } else {
          this.close();
        }
        // The rung that acts CLAIMS the press, so the layer beneath never closes on it too.
        claimEscape(event);
        break;

      case 'ArrowLeft':
        // An alt-modified arrow is the browser's Back/Forward chord on Windows/Linux — it never
        // walks items and is never consumed (alt-modified NON-arrows still reach the registry's
        // alt rows through their own cases below).
        if (event.altKey) break;
        // Inside a document the arrows mean PAGES: the mounted pdf viewer owns them, and the
        // preview's item navigation stands down while the document shows.
        if (this.pdfViewerOwnsArrows()) break;
        this.previewService.prev();
        event.preventDefault();
        break;

      case 'ArrowRight':
        if (event.altKey) break;
        if (this.pdfViewerOwnsArrows()) break;
        this.previewService.next();
        event.preventDefault();
        break;

      case '+':
      case '=':
        // An alt-modified glyph belongs to the registry's alt rows (a Czech Alt+1 arrives as "+").
        if (event.altKey) { dispatchToRegistry(); break; }
        this.zoomIn();
        event.preventDefault();
        break;

      case '-':
        if (event.altKey) { dispatchToRegistry(); break; }
        this.zoomOut();
        event.preventDefault();
        break;

      case 'i':
        this.previewService.togglePanel();
        event.preventDefault();
        break;

      case 'f':
        this.toggleFullscreen();
        event.preventDefault();
        break;

      case 'd':
        this.download();
        event.preventDefault();
        break;

      default:
        // Route unhandled keys to the registry so context-provided bindings (vote-bar and rank
        // keys) can fire. Keys the switch above consumes (Escape, arrows, +/-, i, f, d) NEVER
        // reach the registry — a context binding on one of them is dead; extend the switch instead.
        dispatchToRegistry();
        break;
    }
  }

  /**
   * The arrows page the document while the pdf viewer is mounted AND actually has pages to flip —
   * the mount exists only for a ready PDF whose URL has resolved (the fetch window just after
   * open shows a spinner, and arrows must keep walking items there), and the last conjunct
   * extends the same rule one layer down: while pdf.js is still parsing, or after it failed on a
   * corrupt document, the viewer cannot flip anything and item navigation keeps the arrows. The
   * content-type conjunct is load-bearing: the tile state is unified and reads 'ready' for an
   * image or a video too.
   */
  private pdfViewerOwnsArrows(): boolean {
    return this.previewService.contentType() === 'pdf' && this.pdfRenderState() === 'ready' && !!this.documentUrl()
      && this.pdfReadyUrl() === this.documentUrl();
  }

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Every member dismiss of the overlay (the toolbar X, the document Escape, the backdrop click)
   * lands here and rides the service's GUARDED close: typed work registered inside the preview
   * asks through the one leave prompt first, and Keep editing leaves the overlay — and its
   * zoom, annotate and pane state — exactly as it was.
   */
  async close(): Promise<void> {
    if (!(await this.previewService.requestClose())) return;
    this.resetZoomPan();
    this.exitAnnotateMode();
    // Pane focus is session state, not preference — a stale index must not survive into the
    // next compare session on this app-root singleton.
    this.focusedPaneRequest.set(0);
  }

  download(format?: string): void {
    const item = this.previewService.item();
    if (!item) return;

    this.downloadService.download([item.id], format || 'original').subscribe({
      next: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translateService.instant('common.download'),
          detail: this.translateService.instant('filePreview.downloadStarted')
        });
      },
      error: (error) => {
        console.error('Download failed:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant('filePreview.downloadError')
        });
      }
    });
  }

  /** The star's three states — true · false · UNKNOWN (the list could not load; the star is disabled). */
  isItemBookmarked(): boolean | undefined {
    const item = this.previewService.item();
    return item ? this.bookmarkService.isBookmarked(item.id) : false;
  }

  protected readonly bookmarkLabelKey = bookmarkLabelKey;

  toggleBookmark(): void {
    const item = this.previewService.item();
    if (item) this.bookmarkService.toggleBookmark(item.id);
  }

  private readonly router = inject(Router);

  /** Share the previewed file — the universal dialog. For a file placed in a Manual article the destination is the
   *  road through the article that HOLDS it, read from the server at the gesture (`holdingArticleRoadOf`), wherever
   *  Share is pressed; for every other file the destination is the road the sharer stands on: a
   *  preview opened over a public container (a gallery, an article — never a folder, a collection or a module home)
   *  shares THROUGH it — the container's URL with the file's preview query, which the URL bridge already wrote and
   *  which a public visitor can open for a gallery's file, where the file's own Drive route is a member's; a preview
   *  standing elsewhere (search, the home, a Drive folder, a workspace, the collections home) shares through the
   *  file's container all the same when the item service knows it and it is a gallery or an article (Robert's ruling
   *  of 2026-09-26: the container road whenever the container is known), and the file's canonical URL only when no
   *  container is known or the container is no road. Either carries the viewed instant in "Time travel" (sharing
   *  the past view is a now-gesture whose link must reproduce exactly what the sharer sees). */
  async openShareDialog(): Promise<void> {
    const item = this.previewService.item();
    if (!item) return;
    const urlId = item.urlId ?? item.id?.slice(0, 8);
    const destination = (urlId && await this.holdingArticleRoadOf(item, urlId))
      || (urlId && previewedFileDestination(this.router.url, urlId))
      || (urlId && await this.containerRoadOf(item, urlId))
      || buildItemUrl(item, this.localeService.slugLocale());
    this.shareDialogService.open({
      destinationUrl: shareUrl(destination, this.temporal.isoValue()),
      destinationTitle: this.localeService.editorialLabel(item),
      subjectRecordId: item.id.toString(),
      publicState: item.publicState ?? null,
    });
  }

  /** The road through the article that HOLDS a file placed in a Manual article — the server's answer, asked at the
   *  share gesture and never at open (a placed file hangs in a file group inside its article, so nothing the preview
   *  holds names the article, and on the Manual's page the address in the bar may name another article of the same
   *  page; Robert's rulings of 2026-10-04: the link names the holding article wherever Share is pressed). Null when the
   *  server names no article (an unplaced file, a file taken out of its article, an article this reader cannot read)
   *  or when the read FAILS: the road the preview stands on, the container and the file's own route are then the
   *  honest fallbacks, as for the container read below. */
  private async holdingArticleRoadOf(item: Item, fileUrlId: string): Promise<string | null> {
    let article: Item | null;
    try {
      article = await this.itemService.getHoldingArticle(item.id);
    } catch (error) {
      // A failed read is no reason to swallow the share gesture: the holding article is then not known and the
      // rest of the chain decides the road. The fault stays in the console for the trace.
      console.error('The share gesture could not read the article holding the file; sharing the road the preview stands on:', error);
      return null;
    }
    return article ? previewedFileRoadThrough(article, fileUrlId, this.localeService.slugLocale()) : null;
  }

  /** The road through the previewed file's container when the item service knows the container and it is one of the
   *  public modules' road containers; null when the file is unplaced, the container is unknown to the service, the
   *  read FAILS, or it is no road (a Drive folder). One cached read at the share gesture, never at open. */
  private async containerRoadOf(item: Item, fileUrlId: string): Promise<string | null> {
    if (!item.parentId) return null;
    let container: Item | null;
    try {
      container = await this.itemService.getItem(item.parentId);
    } catch (error) {
      // A container read that fails (the transport, a server fault) is no reason to swallow the share gesture: the
      // container is then not known, and the file's own route under the members pair is the honest road (a refused
      // read already answers null). The fault stays in the console for the trace.
      console.error('The share gesture could not read the file\'s container; sharing the file\'s own route:', error);
      return null;
    }
    return container ? previewedFileRoadThrough(container, fileUrlId, this.localeService.slugLocale()) : null;
  }

  /**
   * The AI-disclosure chip's header mount, for EVERY reader (Robert's ruling of 2026-09-26): gated on the item's own
   * origin — the origin trio the Client's tiles already gate their chips on (made or assisted by AI, synthesized),
   * the one leg of the provenance the public projection serves — never on public mode, so a visitor arriving by link
   * or by a search hit and a member opening the same image both see the disclosure the tile showed. The chip's
   * over-image geometry (the brand's position and scrim) belongs to the portal's imagery; inline in the header the pill
   * stands at full scrim, the origin its one input.
   */
  protected readonly headerAiDisclosure = computed<PublicAiDisclosure | null>(() => {
    const origin = this.previewService.item()?.provenance?.origin;
    if (origin !== 'AI_GENERATED' && origin !== 'AI_ASSISTED' && origin !== 'SYNTHESIZED') return null;
    return { origin, position: 'BOTTOM_LEFT', opacityPercent: 100 };
  });

  /** The preview item's featured state: true · false · UNKNOWN (the full-item read has not landed —
   *  the thin listing item carries no flag, and the star never guesses; the bookmark's shape). */
  protected readonly featuredState = computed(() => this.previewService.item()?.featured);
  protected readonly featuredBusy = signal(false);

  /** The star's tooltip states its DIRECTION — a toggling control names the act it will do. */
  featuredLabelKey(): 'filePreview.unsetFeatured' | 'filePreview.setFeatured' | 'filePreview.featuredUnknown' {
    const state = this.featuredState();
    if (state === undefined) return 'filePreview.featuredUnknown';
    return state ? 'filePreview.unsetFeatured' : 'filePreview.setFeatured';
  }

  /**
   * The star TOGGLES (ruled 2026-09-05): lit when featured, one press un-features — it had been a
   * one-way "set featured" with a static tooltip, so a member who wanted the item out of Featured
   * had to close the preview and find its tile. Optimistic: the star flips at once and the listing
   * hears the change through the preview's item sync; a refused write flips it back and says why.
   * Un-featuring is do-then-undo (ruled 2026-09-06): a mis-click on a curated item is one tap from undone.
   */
  async toggleFeatured(): Promise<void> {
    const item = this.previewService.item();
    if (!item || item.featured === undefined || this.featuredBusy()) return;
    const next = !item.featured;
    this.featuredBusy.set(true);
    this.previewService.updateItem({ ...item, featured: next });
    try {
      const updated = await this.itemService.updateFeatured(item.id, next);
      this.previewService.updateItem(updated);
      if (next) {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('filePreview.featuredSet')
        });
      } else {
        this.featuredUndo.raise({
          detailKey: 'filePreview.featuredUnset',
          refeature: async () => this.previewService.updateItem(await this.itemService.updateFeatured(item.id, true)),
          errorKey: 'filePreview.featuredSetError',
        });
      }
    } catch (error) {
      this.previewService.updateItem(item); // rollback — lands only while this item is still on screen
      console.error('Failed to change featured:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: caughtErrorDetail(error, (k, p) => this.translateService.instant(k, p),
          next ? 'filePreview.featuredSetError' : 'filePreview.featuredUnsetError')
      });
    } finally {
      this.featuredBusy.set(false);
    }
  }

  // ============================================================================
  // Zoom Controls
  // ============================================================================

  zoomIn(): void {
    this.zoom.update(z => Math.min(z * ZOOM_STEP, ZOOM_MAX));
  }

  zoomOut(): void {
    this.zoom.update(z => Math.max(z / ZOOM_STEP, ZOOM_MIN));
    this.clearPanWhenNotZoomedIn();
  }

  zoomReset(): void {
    this.resetZoomPan();
  }

  /** Preset menu behind the percent readout — step with −/+ or jump straight to a level.
   *  Percentages are FIT-relative (100% = fits the window): the viewer renders the
   *  sized-down large preview, so Photoshop-style actual-pixel percentages would
   *  reference original pixels that are not loaded. Built in a computed that reads the
   *  UI-language signal FIRST — `translate.instant` alone would freeze the "Fit" label
   *  on the boot language. */
  protected readonly zoomPresetItems = computed<MenuItem[]>(() => {
    this.localeService.activeUILanguage();
    return [
      { label: this.translateService.instant('filePreview.zoomFit'), command: () => this.setZoomPreset('fit') },
      ...[0.5, 1, 2, 4].map(z => ({ label: `${z * 100}%`, command: () => this.setZoomPreset(z) })),
    ];
  });

  /** Jump to a preset zoom level. 'fit' is the full reset; numeric presets clamp to the
   *  zoom range and clear any stranded pan offset when landing at ≤100% (drag is
   *  disabled there, so a leftover offset would be unrecoverable — same rule as zoomOut). */
  setZoomPreset(preset: number | 'fit'): void {
    if (preset === 'fit') {
      this.resetZoomPan();
      return;
    }
    this.zoom.set(Math.max(ZOOM_MIN, Math.min(preset, ZOOM_MAX)));
    this.clearPanWhenNotZoomedIn();
  }

  private resetZoomPan(): void {
    this.zoom.set(1);
    this.pan.set({ x: 0, y: 0 });
    this.isPanning.set(false);
    this.panPointerId = null;
  }

  /** Panning only exists while zoomed in — leaving that state clears any offset,
   *  since drag is disabled at ≤100% and a stranded offset would be unrecoverable. */
  private clearPanWhenNotZoomedIn(): void {
    if (this.zoom() <= 1) {
      this.pan.set({ x: 0, y: 0 });
    }
  }

  onWheel(event: WheelEvent): void {
    if (this.previewService.contentType() !== 'image') return;

    event.preventDefault();
    const delta = event.deltaY > 0 ? 1 / 1.1 : 1.1;
    this.zoom.update(z => Math.max(ZOOM_MIN, Math.min(z * delta, ZOOM_MAX)));
    this.clearPanWhenNotZoomedIn();
  }

  // ============================================================================
  // Pan (drag while zoomed in) + annotate gesture — native pointer events.
  // The handlers BRANCH on annotate mode: while annotating, the pointer draws a
  // draft anchor and pan is suspended; otherwise the shipped pan behavior runs.
  // ============================================================================

  onImagePointerDown(event: PointerEvent): void {
    if (this.annotateMode()) {
      this.beginAnnotationDraft(event);
      return;
    }
    if (this.zoom() <= 1) return;
    this.panPointerId = event.pointerId;
    this.panStart = { x: event.clientX, y: event.clientY };
    this.panOrigin = this.pan();
    this.isPanning.set(true);
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  onImagePointerMove(event: PointerEvent): void {
    if (this.annotationDraft() && event.pointerId === this.annotationPointerId) {
      this.updateAnnotationDraft(event);
      return;
    }
    if (!this.isPanning() || event.pointerId !== this.panPointerId) return;
    this.pan.set({
      x: this.panOrigin.x + (event.clientX - this.panStart.x),
      y: this.panOrigin.y + (event.clientY - this.panStart.y),
    });
  }

  onImagePointerUp(event: PointerEvent): void {
    if (this.annotationDraft() && event.pointerId === this.annotationPointerId) {
      this.finishAnnotationDraft(event);
      return;
    }
    if (event.pointerId !== this.panPointerId) return;
    this.panPointerId = null;
    this.isPanning.set(false);
  }

  /** A cancelled annotate gesture places nothing (unlike pointer-up, which finalizes);
   *  a cancelled pan just ends. */
  onImagePointerCancel(event: PointerEvent): void {
    if (this.annotationDraft() && event.pointerId === this.annotationPointerId) {
      this.annotationDraft.set(null);
      this.annotationPointerId = null;
      this.annotationDownScreen = null;
      return;
    }
    this.onImagePointerUp(event);
  }

  // ============================================================================
  // Image annotations — mode + gesture
  // ============================================================================

  protected toggleAnnotateMode(): void {
    if (this.annotateMode()) {
      this.exitAnnotateMode();
      return;
    }
    if (!this.annotateAvailable()) return;
    this.annotateMode.set(true);
  }

  /** Leaves annotate mode and discards any in-flight draft AND any pending (captured,
   *  not-yet-posted) anchor — backing out of annotating abandons the mark; the composer's
   *  chip disappears with it (one state, two views). Typed comment text is untouched. */
  protected exitAnnotateMode(): void {
    this.annotateMode.set(false);
    this.annotationDraft.set(null);
    this.annotationPointerId = null;
    this.annotationDownScreen = null;
    this.previewService.pendingAnchor.set(null);
  }

  /** Captures the image's natural pixel size — the fraction-coordinate basis. */
  protected onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.naturalSize.set({ width: img.naturalWidth, height: img.naturalHeight });
  }

  private measureViewerBox(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    // Content box, not border box: the img fits within rect MINUS the padding (global
    // border-box sizing), so the padding must come off the fit-math dimensions — and its
    // top-left offsets become the content origin inside the overlay's coordinate space.
    const cs = getComputedStyle(el);
    const padLeft = parseFloat(cs.paddingLeft) || 0;
    const padTop = parseFloat(cs.paddingTop) || 0;
    const padRight = parseFloat(cs.paddingRight) || 0;
    const padBottom = parseFloat(cs.paddingBottom) || 0;
    this.viewerPad.set({ x: padLeft, y: padTop });
    this.viewerBox.set({
      left: rect.left, top: rect.top,
      width: rect.width - padLeft - padRight,
      height: rect.height - padTop - padBottom,
    });
  }

  private beginAnnotationDraft(event: PointerEvent): void {
    // Re-measure at gesture start — the container can MOVE (panel toggle, window drag)
    // without resizing, and the resize observer only sees size changes.
    const el = this.viewerImageEl()?.nativeElement;
    if (el) this.measureViewerBox(el);
    const fraction = this.clientPointToFraction(event.clientX, event.clientY);
    if (!fraction) return;
    this.annotationPointerId = event.pointerId;
    this.annotationDownScreen = { x: event.clientX, y: event.clientY };
    this.annotationDraft.set({ start: fraction, current: fraction });
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  private updateAnnotationDraft(event: PointerEvent): void {
    const fraction = this.clientPointToFraction(event.clientX, event.clientY);
    if (!fraction) return;
    this.annotationDraft.update(d => (d ? { ...d, current: fraction } : d));
  }

  private finishAnnotationDraft(event: PointerEvent): void {
    const draft = this.annotationDraft();
    const down = this.annotationDownScreen;
    this.annotationDraft.set(null);
    this.annotationPointerId = null;
    this.annotationDownScreen = null;
    if (!draft || !down) return;
    const release = this.clientPointToFraction(event.clientX, event.clientY);
    const inImage = (p: Point | null): p is Point =>
      !!p && p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1;
    // Released outside the image = the gesture cancels. Never clamp — a clamped pin
    // would mark a place the author never pointed at (the server rejects out-of-range
    // anchors for the same reason).
    if (!inImage(release) || !inImage(draft.start)) return;
    const dragDistance = Math.hypot(event.clientX - down.x, event.clientY - down.y);
    if (dragDistance < ANNOTATION_DRAG_THRESHOLD_PX) {
      this.completeAnnotationGesture({ kind: 'POINT', x: draft.start.x, y: draft.start.y });
      return;
    }
    this.completeAnnotationGesture({
      kind: 'REGION',
      x: Math.min(draft.start.x, release.x),
      y: Math.min(draft.start.y, release.y),
      width: Math.abs(release.x - draft.start.x),
      height: Math.abs(release.y - draft.start.y),
    });
  }

  /** Hands the captured anchor to the composer: pending anchor + Comments tab request
   *  + un-collapse the panel. The info panel consumes the tab request (its tab state
   *  is component-local) and the thread clears the anchor after the post. */
  private completeAnnotationGesture(anchor: CommentAnchor): void {
    this.previewService.pendingAnchor.set(anchor);
    this.previewService.requestedPanelTab.set('comments');
    this.previewService.setPanel(false);
  }

  /** Page producer: an annotate-mode click on a PDF page's comment layer. */
  protected onPdfAnnotationClick(point: { page: number; x: number; y: number }): void {
    this.completeAnnotationGesture({ kind: 'PAGE', page: point.page, x: point.x, y: point.y });
  }

  /** Frame producer: a completed annotate gesture on the paused video — a moment plus
   *  WHERE on the frame it points (point or region). */
  protected onVideoAnnotationCapture(capture: { x: number; y: number; width?: number; height?: number; timecode: number }): void {
    this.completeAnnotationGesture({
      kind: 'TIMECODE',
      timecode: Math.max(0, capture.timecode),
      x: capture.x, y: capture.y,
      width: capture.width, height: capture.height,
    });
  }

  /** The composer asked for the current playback moment (focus on a video preview, or
   *  the chip's re-arm): pause — the stamp must equal the frame on screen — and park a
   *  plain TIMECODE anchor. A mark already in progress is never overwritten. */
  private readonly timestampCaptureEffect = effect(() => {
    const tick = this.previewService.timestampCaptureRequested();
    if (tick === 0) return;
    untracked(() => {
      if (this.previewService.contentType() !== 'video') return;
      if (this.previewService.pendingAnchor()) return;
      if (!commentsArmed(this.previewService.context())) return;
      if (this.temporal.active() || this.access.isReadOnlyRoot() || this.publicMode()) return;
      const player = this.videoPlayerCmp();
      if (!player) return;
      player.pauseVideo();
      this.previewService.pendingAnchor.set({ kind: 'TIMECODE', timecode: Math.max(0, player.currentTime()) });
    });
  });

  // ============================================================================
  // Image annotations — activation linking (chips ↔ pins)
  // ============================================================================

  /** Route a chip activation to its media: pages scroll, timecodes seek, image
   *  points highlight their pin (everything on an image is already in view). */
  private navigateToAnchor(commentId: string, anchor: CommentAnchor): void {
    switch (anchor.kind) {
      case 'PAGE':
        if (anchor.page != null) this.navigateToPage(anchor.page);
        break;
      case 'TIMECODE':
      case 'TIMECODE_RANGE':
        if (anchor.timecode != null) this.navigateToTimecode(anchor.timecode);
        break;
      case 'POINT':
      case 'REGION':
        this.previewService.activeAnnotationId.set(commentId);
        break;
      // TEXT_RANGE renders nowhere yet (ruled deferral) — nothing to navigate.
    }
  }

  protected navigateToPage(page: number): void {
    this.pdfViewer()?.goToPage(page);
  }

  /**
   * The PDF engine's chunk could not be loaded (an older browser that cannot evaluate it, or a
   * broken connection). Angular keeps a failed deferred load failed for the page's lifetime, so
   * the only honest retry is a reload; the preview's URL bridge reopens this same preview. A
   * reload is a full-page exit, so typed work in the preview (a caption, a description, an
   * annotation) is asked about first through the one leave prompt every exit takes.
   */
  protected reloadForPdfEngine(): void {
    void this.leavePrompt.confirmLeaveIfDirty(() => {
      window.location.reload();
    });
  }

  /**
   * Asked only after the engine's chunk has failed: a browser without the engine's global (Safari before
   * 18.4) gets a plain notice that PDF previews need a newer browser and no retry, because a reload could
   * never work there; everything else in the preview stays as it is. Evaluated once, the environment does
   * not change while the preview is open.
   */
  protected readonly pdfEngineSupported = pdfEngineCanRun();

  protected navigateToTimecode(seconds: number): void {
    this.videoPlayer()?.seekTo(seconds);
  }

  /** A pin on the image overlay was clicked — open the thread and mark its comment. */
  protected onOverlayPinClick(commentId: string): void {
    this.previewService.activeAnnotationId.set(commentId);
    this.previewService.requestedPanelTab.set('comments');
    this.previewService.setPanel(false);
  }

  /** Pin hover — mirror the highlight onto the thread's chip. */
  protected onOverlayPinHover(commentId: string, hovering: boolean): void {
    this.previewService.activeAnnotationId.set(hovering ? commentId : null);
  }

  private clientPointToFraction(clientX: number, clientY: number): Point | null {
    const box = this.viewerBox();
    const base = this.annotationBaseBox();
    if (!box || !base) return null;
    return screenToImageFraction({ x: clientX - box.left, y: clientY - box.top }, base, this.zoom(), this.pan());
  }

  /** The annotation face's Try again — the SAME pin read for the media on screen. */
  retryAnnotationComments(): void {
    const item = this.previewService.item();
    const ctx = this.previewService.context();
    if (!item || !commentsArmed(ctx)) return;
    // A retry is a NEW pin-load run: the token moves so an older run's late failure can never
    // re-raise the face over pins this run loads.
    this.anchoredReadToken++;
    void this.loadAnnotationComments(threadParentFor(ctx, item).parentId);
  }

  /** Pin data fetch — mirrors the thread's own first page so ordinals stay aligned. */
  private async loadAnnotationComments(parentId: string): Promise<void> {
    const token = this.anchoredReadToken;
    // The failure mark clears only when this read LANDS for the media still on screen: cleared at
    // entry, a retry painted un-annotated media for the length of the round trip. The face stays;
    // its button says it is working.
    this.annotationsReloading.set(true);
    try {
      // The anchored-only read: EVERY pin on this media, not just the thread's first page —
      // chip activation for a beyond-page comment finds its pin.
      const list = await this.commentService.getAnchoredForParent(parentId);
      // Race guards: publish only if this thread is still the current one AND no later
      // pin-load run superseded this read — a stale read must never re-show a deleted
      // pin or back-fill an overlay that "Time travel" emptied.
      const current = this.previewService.item();
      const ctx = this.previewService.context();
      if (token !== this.anchoredReadToken) return; // a later pin-load run superseded this read
      // The read landed for the run still in force: the face stands down whatever the thread did
      // meanwhile; the pins paint only for the thread that asked for them.
      this.annotationsLoadFailed.set(false);
      if (current && commentsArmed(ctx) && threadParentFor(ctx, current).parentId === parentId) {
        this.annotationComments.set(list);
      }
    } catch (error) {
      // The pins are content on the media: a failed read leaves the image, video or PDF looking
      // un-annotated, which is a false claim — the face says so, for the read that is still current.
      console.error('Failed to load annotation comments:', error);
      if (token === this.anchoredReadToken) this.annotationsLoadFailed.set(true);
    } finally {
      if (token === this.anchoredReadToken) this.annotationsReloading.set(false);
    }
  }

  // ============================================================================
  // Fullscreen
  // ============================================================================

  toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  // ============================================================================
  // Video
  // ============================================================================

  async loadVideo(): Promise<void> {
    const item = this.previewService.item();
    if (!item) return;

    this.videoLoading.set(true);
    this.videoData.set(null);
    // A retry must clear the face it retries: the error clears at entry (the service clears it
    // only on item change), so a successful Try again never leaves the face beside the player.
    this.previewService.setError(null);

    try {
      const result = await this.itemService.getVideoStreams(item.id);
      if (result) {
        this.videoData.set(result);
      }
    } catch (error) {
      console.error('Failed to load video:', error);
      // The in-body face owns the failure's voice — no toast beside it.
      this.previewService.setError(
        caughtErrorDetail(error, (k, p) => this.translateService.instant(k, p), 'filePreview.videoLoadError'));
    } finally {
      this.videoLoading.set(false);
    }
  }

  // ============================================================================
  // URL Helpers
  // ============================================================================

  /** Lightbox-size preview (large). Returns empty string when no preview exists. */
  getPreviewUrl(item: Item | null): string {
    if (!item) return '';
    return getPreviewUrl(item, 'large', this.configService) ?? '';
  }

  // ============================================================================
  // Item Updates (from sub-components)
  // ============================================================================

  onItemUpdated(updatedItem: Item): void {
    this.previewService.updateItem(updatedItem);
  }

  // ============================================================================
  // Overlay Click
  // ============================================================================

  onOverlayClick(event: MouseEvent): void {
    // Close when clicking on the overlay background
    const target = event.target as HTMLElement;
    if (target.classList.contains('preview-overlay')) {
      this.close();
    }
  }

  // ============================================================================
  // Filmstrip
  // ============================================================================

  onFilmstripItemClick(index: number): void {
    this.previewService.goTo(index);
  }

  /** Toggle pin state from a filmstrip thumbnail badge — doesn't navigate (stopPropagation). */
  togglePinFilmstrip(item: Item, ev: MouseEvent): void {
    ev.stopPropagation();
    if (this.previewService.pinnedIds().has(item.id)) {
      this.previewService.unpinFromCompare(item.id);
    } else {
      this.previewService.pinToCompare(item.id);
    }
  }

  getThumbnailUrl(item: Item): string {
    return getPreviewUrl(item, 'small', this.configService) ?? '';
  }

  /** Filmstrip item IDs whose thumbnail image failed to load — fall back to a type icon. */
  private readonly failedThumbnails = signal<ReadonlySet<string>>(new Set());

  /** True when the item has no usable thumbnail URL or its image errored — show a type icon. */
  showThumbnailFallback(item: Item): boolean {
    return !this.getThumbnailUrl(item) || this.failedThumbnails().has(item.id);
  }

  /** Record a thumbnail load failure so the template swaps in the type icon. */
  onThumbnailError(item: Item): void {
    this.failedThumbnails.update(set => {
      if (set.has(item.id)) return set;
      const next = new Set(set);
      next.add(item.id);
      return next;
    });
  }

  /** Type-aware icon + color for a filmstrip item lacking a thumbnail. */
  getItemIcon = getItemIcon;
  getItemIconColor = getItemIconColor;

  // ============================================================================
  // Bottom Sheet (Mobile)
  // ============================================================================

  toggleBottomSheet(): void {
    this.bottomSheetExpanded.update(v => !v);
  }

  onBottomSheetDragStart(event: TouchEvent | MouseEvent): void {
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    this.bottomSheetDragStartY = clientY;

    const sheet = this.bottomSheetRef()?.nativeElement;
    if (sheet) {
      this.bottomSheetStartHeight = sheet.offsetHeight;
    }
  }

  onBottomSheetDrag(event: TouchEvent | MouseEvent): void {
    if (this.bottomSheetDragStartY === 0) return;

    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    const deltaY = this.bottomSheetDragStartY - clientY;

    // Determine direction and threshold
    const threshold = 50;
    if (Math.abs(deltaY) > threshold) {
      if (deltaY > 0 && !this.bottomSheetExpanded()) {
        // Swiping up - expand
        this.bottomSheetExpanded.set(true);
      } else if (deltaY < 0 && this.bottomSheetExpanded()) {
        // Swiping down - collapse
        this.bottomSheetExpanded.set(false);
      }
      this.bottomSheetDragStartY = 0;
    }
  }

  onBottomSheetDragEnd(): void {
    this.bottomSheetDragStartY = 0;
  }

  // ============================================================================
  // Dots Navigation (Mobile)
  // ============================================================================

  onDotClick(index: number): void {
    this.previewService.goTo(index);
  }

  /** Get visible dots range for pagination (max 7 dots) */
  getVisibleDotsRange(): number[] {
    const items = this.previewService.items();
    const currentIndex = this.previewService.currentIndex();
    const total = items.length;
    const maxDots = 7;

    if (total <= maxDots) {
      return Array.from({ length: total }, (_, i) => i);
    }

    // Calculate range centered on current index
    let start = Math.max(0, currentIndex - Math.floor(maxDots / 2));
    const end = Math.min(total, start + maxDots);

    // Adjust start if we're near the end
    if (end - start < maxDots) {
      start = Math.max(0, end - maxDots);
    }

    return Array.from({ length: end - start }, (_, i) => start + i);
  }
}
